import React, { useState } from 'react';
import { ArrowLeftIcon, FunnelIcon, EyeIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { useAuth } from '../contexts/AuthContext';
import { useApi } from '../hooks/useApi';
import LoadingSpinner from '../components/UI/LoadingSpinner';

interface FeedLog {
  id: string;
  pond_id: string;
  feed_date: string;
  quantity_kg: number;
  feed_brand: string;
  feed_type?: string;
  remarks?: string;
  created_at: string;
  created_by: string;
  pond_name?: string;
}

const FeedLogs: React.FC = () => {
  const { selectedLocation } = useLocation();
  const { user } = useAuth();
  
  // Set default date range to last 7 days
  const getDefaultDates = () => {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);
    
    return {
      startDate: sevenDaysAgo.toISOString().split('T')[0],
      endDate: today.toISOString().split('T')[0]
    };
  };
  
  const defaultDates = getDefaultDates();
  const [startDate, setStartDate] = useState(defaultDates.startDate);
  const [endDate, setEndDate] = useState(defaultDates.endDate);
  const [selectedPond, setSelectedPond] = useState('');
  const [selectedFeedBrand, setSelectedFeedBrand] = useState('');

  // Build the API endpoint with query parameters
  const buildFeedLogsEndpoint = () => {
    if (!selectedLocation) return null;
    
    let endpoint = `/locations/${selectedLocation.id}/feed-logs`;
    const params = [];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (selectedFeedBrand) params.push(`feed_brand=${selectedFeedBrand}`);
    
    if (params.length > 0) {
      endpoint += `?${params.join('&')}`;
    }
    
    return endpoint;
  };

  const { data: pondsData } = useApi(`/locations/${selectedLocation?.id}/ponds`);
  const { data: feedBrandsData } = useApi<any[]>(
    selectedLocation?.id ? `/master-data/feed_brands?location_id=${encodeURIComponent(selectedLocation.id)}` : '',
    {
      dependencies: [selectedLocation?.id],
      skip: !selectedLocation
    }
  );
  const { data: feedLogsData, loading, error, refetch } = useApi<FeedLog[]>(
    buildFeedLogsEndpoint() || '',
    { 
      dependencies: [selectedLocation?.id, startDate, endDate, selectedPond, selectedFeedBrand],
      skip: !selectedLocation
    }
  );

  // Fetch total count separately
  const buildFeedLogsCountEndpoint = () => {
    if (!selectedLocation) return null;
    let endpoint = `/locations/${selectedLocation.id}/feed-logs/count`;
    const params = [] as string[];
    if (startDate) params.push(`start_date=${startDate}`);
    if (endDate) params.push(`end_date=${endDate}`);
    if (selectedPond) params.push(`pond_id=${selectedPond}`);
    if (selectedFeedBrand) params.push(`feed_brand=${selectedFeedBrand}`);
    if (params.length > 0) endpoint += `?${params.join('&')}`;
    return endpoint;
  };

  const { data: feedLogsCountData } = useApi<{ total: number }>(
    buildFeedLogsCountEndpoint() || '',
    {
      dependencies: [selectedLocation?.id, startDate, endDate, selectedPond, selectedFeedBrand],
      skip: !selectedLocation
    }
  );

  // Debug logging
  React.useEffect(() => {
    console.log('FeedLogs Debug:', {
      selectedLocation: selectedLocation?.id,
      endpoint: buildFeedLogsEndpoint(),
      feedLogsData,
      error,
      loading,
      apiBaseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:8103/api'
    });
  }, [selectedLocation?.id, feedLogsData, error, loading]);

  // Enrich feed logs with pond names
  const enrichedFeedLogs = React.useMemo(() => {
    if (!feedLogsData || !pondsData) return [];
    
    return feedLogsData.map((log: FeedLog) => ({
      ...log,
      pond_name: (pondsData as any[]).find((p: any) => p.id === log.pond_id)?.name || 'Unknown Pond'
    }));
  }, [feedLogsData, pondsData]);

  const clearFilters = () => {
    const defaultDates = getDefaultDates();
    setStartDate(defaultDates.startDate);
    setEndDate(defaultDates.endDate);
    setSelectedPond('');
    setSelectedFeedBrand('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTotalQuantity = () => {
    return enrichedFeedLogs.reduce((sum, log) => sum + log.quantity_kg, 0).toFixed(1);
  };

  const getUniqueBrands = () => {
    const brands = Array.from(new Set(enrichedFeedLogs.map(log => log.feed_brand)));
    return brands.length;
  };

  // Parse date string to Date object, handling multiple formats
  // Prioritizes DD/MM/YYYY format (common in India)
  const parseDateString = (dateStr: string): Date | null => {
    if (!dateStr) return null;
    
    // Remove time part if present
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    
    // Check if it's already in ISO format (YYYY-MM-DD)
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) {
      return new Date(dateOnly + 'T00:00:00');
    }
    
    // Try DD/MM/YYYY format (common in India)
    const slashMatch = dateOnly.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (slashMatch) {
      const [, part1, part2, year] = slashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      // If first part > 12, it must be DD/MM/YYYY
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1);
      }
      // If second part > 12, it must be MM/DD/YYYY
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2);
      }
      // Ambiguous case: try DD/MM/YYYY first (more common in India)
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM; // DD/MM/YYYY
      }
      // Otherwise try MM/DD/YYYY
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Try other formats with dashes
    const dashMatch = dateOnly.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (dashMatch) {
      const [, part1, part2, year] = dashMatch;
      const num1 = parseInt(part1);
      const num2 = parseInt(part2);
      
      if (num1 > 12) {
        return new Date(parseInt(year), num2 - 1, num1); // DD-MM-YYYY
      }
      if (num2 > 12) {
        return new Date(parseInt(year), num1 - 1, num2); // MM-DD-YYYY
      }
      // Try DD-MM-YYYY first
      const dateAsDDMM = new Date(parseInt(year), num2 - 1, num1);
      if (dateAsDDMM.getDate() === num1 && dateAsDDMM.getMonth() === num2 - 1) {
        return dateAsDDMM;
      }
      return new Date(parseInt(year), num1 - 1, num2);
    }
    
    // Fallback to Date constructor
    const parsed = new Date(dateOnly);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  // Normalize date to ISO format (YYYY-MM-DD) for grouping
  const normalizeDateToISO = (dateStr: string): string => {
    if (!dateStr) return '';
    
    const date = parseDateString(dateStr);
    if (!date) {
      console.warn('Failed to parse date:', dateStr);
      // If it's already in ISO format, return it
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        return dateStr;
      }
      return dateStr; // Return original if parsing fails
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Format date for display
  const formatDateForDisplay = (dateStr: string): string => {
    const date = parseDateString(dateStr);
    if (!date) return dateStr;
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Group feed logs by date and calculate daily summaries
  const getGroupedLogsByDate = () => {
    const grouped: { [date: string]: { 
      logs: FeedLog[]; 
      totalKg: number; 
      displayDate: string;
      byBrand: { [brand: string]: number };
    } } = {};
    
    enrichedFeedLogs.forEach((log) => {
      // Normalize date to ISO format for grouping - use feed_date, not created_at
      const normalizedDate = normalizeDateToISO(log.feed_date);
      
      if (!normalizedDate) {
        // Skip logs with invalid dates
        console.warn('Skipping log with invalid feed_date:', log.feed_date, log.id);
        return;
      }
      
      if (!grouped[normalizedDate]) {
        // Use the first log's feed_date for display date
        grouped[normalizedDate] = { 
          logs: [], 
          totalKg: 0, 
          displayDate: formatDateForDisplay(log.feed_date),
          byBrand: {}
        };
      }
      
      grouped[normalizedDate].logs.push(log);
      grouped[normalizedDate].totalKg += log.quantity_kg || 0;
      
      // Track by brand
      const brand = log.feed_brand || 'Unknown';
      if (!grouped[normalizedDate].byBrand[brand]) {
        grouped[normalizedDate].byBrand[brand] = 0;
      }
      grouped[normalizedDate].byBrand[brand] += log.quantity_kg || 0;
    });
    
    // Convert to array and sort by date (newest first)
    return Object.entries(grouped)
      .map(([date, data]) => ({ date, ...data }))
      .sort((a, b) => b.date.localeCompare(a.date));
  };

  if (!selectedLocation) {
    return (
      <div className="max-w-md mx-auto bg-gray-50 min-h-screen py-8 text-center">
        <p className="text-gray-600">Please select a location first</p>
        <Link to="/locations" className="text-blue-600 hover:text-blue-800">
          Go to Location Selection
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 sticky top-0 z-30">
        <div className="flex items-center gap-2 mb-3">
          <Link to="/data-entry" className="p-1 text-gray-400 hover:text-gray-600">
            <ArrowLeftIcon className="h-5 w-5" />
          </Link>
          <h1 className="text-lg font-bold text-gray-800">🌾 Feed Logs</h1>
        </div>

        {/* Filters */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FunnelIcon className="h-4 w-4 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Filters</span>
            </div>
            <div className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
              Last 7 days
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">From Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">To Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Pond</label>
              <select
                value={selectedPond}
                onChange={(e) => setSelectedPond(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              >
                <option value="">All Ponds</option>
                {(pondsData as any[] || []).map((pond: any) => (
                  <option key={pond.id} value={pond.id}>
                    {pond.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Feed Brand</label>
              <select
                value={selectedFeedBrand}
                onChange={(e) => setSelectedFeedBrand(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg text-xs"
              >
                <option value="">All Brands</option>
                {(feedBrandsData as any[] || []).map((brand: any) => (
                  <option key={brand.id} value={brand.name}>
                    {brand.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={clearFilters}
              className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-xs font-medium hover:bg-gray-200 transition-colors"
            >
              Reset to 7 Days
            </button>
            <button
              onClick={refetch}
              className="flex-1 bg-blue-600 text-white py-2 px-3 rounded-lg text-xs font-medium hover:bg-blue-700 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        {enrichedFeedLogs.length > 0 && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="bg-blue-50 p-2 rounded-lg">
              <div className="text-xs text-blue-600 font-medium">Total Entries</div>
              <div className="text-sm font-bold text-blue-700">{feedLogsCountData?.total ?? enrichedFeedLogs.length}</div>
            </div>
            <div className="bg-green-50 p-2 rounded-lg">
              <div className="text-xs text-green-600 font-medium">Total Feed (kg)</div>
              <div className="text-sm font-bold text-green-700">{getTotalQuantity()}</div>
            </div>
            <div className="bg-purple-50 p-2 rounded-lg">
              <div className="text-xs text-purple-600 font-medium">Brands Used</div>
              <div className="text-sm font-bold text-purple-700">{getUniqueBrands()}</div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg m-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="p-8 text-center">
          <LoadingSpinner size="md" />
          <p className="text-gray-500 mt-2">Loading feed logs...</p>
        </div>
      )}

      {/* Feed Logs List */}
      {!loading && (
        <div className="p-4 space-y-3">
          {enrichedFeedLogs.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">🌾</div>
              <p className="text-gray-500 mb-2">No feed logs found</p>
              <p className="text-sm text-gray-400">Try adjusting your filters or add some feed entries.</p>
              {user?.role !== 'user' && (
                <Link 
                  to="/data-entry/feed" 
                  className="inline-block mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 transition-colors"
                >
                  Add Feed Entry
                </Link>
              )}
            </div>
          ) : (
            getGroupedLogsByDate().map((dayGroup) => (
              <div key={dayGroup.date} className="space-y-2">
                {/* Daily Summary Strip */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <span className="font-semibold text-gray-700">
                          {dayGroup.displayDate}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="text-xs font-medium text-gray-600">
                          Total: <span className="font-bold text-green-700">{dayGroup.totalKg.toFixed(1)} kg</span>
                        </span>
                        {Object.entries(dayGroup.byBrand).map(([brand, kg]) => (
                          <span key={brand} className="text-xs text-gray-600">
                            {brand}: <span className="font-semibold text-gray-700">{kg.toFixed(1)} kg</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Individual Feed Logs for this day */}
                {dayGroup.logs.map((log) => (
                  <div key={log.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 ml-2">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-800">{log.pond_name}</h3>
                        <p className="text-sm text-gray-500">{formatDateForDisplay(log.feed_date)}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-lg font-bold text-blue-600">{log.quantity_kg} kg</div>
                        <div className="text-xs text-gray-500">{log.feed_brand}</div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {log.feed_type && (
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600">Type:</span>
                          <span className="text-xs text-gray-800">{log.feed_type}</span>
                        </div>
                      )}
                      
                      {log.remarks && (
                        <div className="flex items-start gap-2">
                          <span className="text-xs font-medium text-gray-600">Remarks:</span>
                          <span className="text-xs text-gray-800 flex-1">{log.remarks}</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-xs text-gray-400 pt-2 border-t border-gray-100">
                        <span>Created: {formatDateTime(log.created_at)}</span>
                        <div className="flex items-center gap-1">
                          <EyeIcon className="h-3 w-3" />
                          <span>View Details</span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>
      )}

      {/* Floating Action Button */}
      {user?.role !== 'user' && (
        <div className="fixed bottom-6 right-6">
          <Link
            to="/data-entry/feed"
            className="bg-blue-600 text-white p-4 rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
          >
            <span className="text-xl">🌾</span>
            <span className="text-sm font-medium">Add Feed</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default FeedLogs;
